import React, { useCallback, useContext, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import Tooltip from '@totem/components/common/tooltip/Tooltip';
import { sortStringAscending } from '@totem/components/services/threats/eventsContainer_legacy/utilities';
import AuvikContext from '@totem/components/settings/sections/auvikMapping/AuvikContext';
import { TenantReportRecord } from '@totem/components/settings/sections/auvikMapping/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { AUVIK_ENDPOINT } from '@totem/utilities/endpoints';

const styles = {
  iconButton: {
    margin: '0 2px',
  },
};

const AuvikMappingTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { data, loading, onAction, onActionComplete } =
    useContext(AuvikContext);
  const [isSending, setIsSending] = useState(false);

  const sendDeleteTenant = useCallback(
    async (configId: string, tenantId: number) => {
      if (isSending) {
        return;
      }

      setIsSending(true);

      fetch(`${AUVIK_ENDPOINT}/tenant/${configId}/${tenantId}`, {
        method: 'DELETE',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => {
          if (res.status < 400) {
            onActionComplete(true);
          } else {
            notification.error({
              title: 'Error tenant delete!',
              description: 'An error occurred while deleting tenant.',
              duration: 0,
            });

            onActionComplete(false);
          }
        })
        .then(() => {
          setIsSending(false);
        });
    },
    [isSending],
  );

  const onDelete = (tenant: TenantReportRecord) => {
    sendDeleteTenant(tenant.id, tenant.tenant.tenantId);
  };

  const columns: ColumnProps<TenantReportRecord>[] = [
    {
      title: 'Name',
      dataIndex: 'tenant.tenantName',
      key: 'tenant.tenantName',
      sorter: (compA, compB) =>
        sortStringAscending(compA.tenant.tenantName, compB.tenant.tenantName),
      render: (text: string, item: TenantReportRecord) => {
        return item.tenant.tenantName;
      },
    },
    {
      title: 'Domain Prefix',
      dataIndex: 'tenant.tenantDomainPrefix',
      key: 'tenant.tenantDomainPrefix',
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.tenant.tenantDomainPrefix,
          compB.tenant.tenantDomainPrefix,
        ),
      render: (text: string, item: TenantReportRecord) => {
        return item.tenant.tenantDomainPrefix;
      },
    },
    {
      title: 'Building',
      dataIndex: 'building.id',
      key: 'building.id',
      sorter: (compA, compB) =>
        sortStringAscending(compA.building.name, compB.building.name),
      render: (text: string, item: TenantReportRecord) => {
        return typeof item.building !== 'undefined' && item.building !== null
          ? item.building.name
          : '';
      },
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'action',
      render: (_, item: TenantReportRecord) => (
        <div className="center-table-cell">
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Update Building Mapping" placement="top">
              <Button
                icon={<EditOutlined />}
                shape="circle"
                onClick={(): void => onAction('showMappingDialog', item)}
                style={styles.iconButton}
              />
            </Tooltip>
          )}
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Delete Tenant" placement="top">
              <Button
                icon={<IBDIcon name={'Delete'} />}
                shape="circle"
                onClick={(): void => onDelete(item)}
                style={styles.iconButton}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={typeof data !== 'undefined' && data !== null ? data : []}
      loading={loading}
      rowKey={(record) =>
        `${record.id}-${record.tenant.organizationId}-${record.tenant.tenantId ?? 'no-building'}`
      }
      pagination={{
        defaultPageSize: 100,
        showSizeChanger: true,
      }}
    />
  );
};

export default AuvikMappingTable;
